import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "security",
  name: "セキュリティ",
  nameEn: "Security",
  seo: {
    title:
      "セキュリティを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "OWASP Top 10、認証認可、暗号化、脆弱性診断、セキュアコーディングを実践的に学べるゆめさくエンジニア。セキュリティエンジニアへのキャリアを目指しましょう。",
    url: "/columns/security-programming-school",
  },
  hero: {
    title: "セキュリティを学べる女性向けプログラミングスクール",
    englishTitle: "Learn Security",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "セキュリティ" },
  ],
  overview: {
    heading: "セキュリティとは？",
    paragraphs: [
      "Webセキュリティとは、Webアプリケーションやシステムを不正アクセス、データ漏洩、サイバー攻撃から守るための技術・手法の総称です。SQLインジェクション、XSS（クロスサイトスクリプティング）、CSRF（クロスサイトリクエストフォージェリ）など、さまざまな攻撃手法に対する防御策を理解し、実装するスキルが求められます。",
      "OWASP（Open Web Application Security Project）が発表する「OWASP Top 10」は、Webアプリケーションで最も危険なセキュリティリスクのランキングで、セキュリティ学習の基本指標となっています。認証・認可の仕組み、暗号化技術、セキュアコーディングの原則を理解することが重要です。",
      "セキュリティはすべてのエンジニアに必要なスキルです。開発初期からセキュリティを考慮した「セキュリティ・バイ・デザイン」のアプローチが業界標準となりつつあり、セキュリティ知識を持つエンジニアの価値は年々高まっています。",
    ],
    useCases: [
      {
        label: "脆弱性診断・ペネトレーションテスト",
        description:
          "Webアプリケーションやインフラに対して疑似的な攻撃を行い、セキュリティ上の弱点を発見・報告します。",
      },
      {
        label: "認証・認可システムの設計",
        description:
          "OAuth 2.0、JWT、多要素認証（MFA）など、安全なユーザー認証・権限管理の仕組みを設計・実装します。",
      },
      {
        label: "セキュアコーディング",
        description:
          "開発段階からSQLインジェクション、XSS、CSRFなどの脆弱性を作り込まないコーディング手法を実践します。",
      },
      {
        label: "インシデント対応・セキュリティ監視",
        description:
          "セキュリティインシデントの検知・分析・対応を行い、システムの安全性を維持するための監視体制を構築します。",
      },
    ],
  },
  whyLearn: {
    heading: "セキュリティを学ぶべき3つの理由",
    items: [
      {
        title: "サイバー攻撃の増加で専門人材が圧倒的に不足",
        description:
          "サイバー攻撃は年々巧妙化・増加しており、セキュリティ人材の不足は世界的な課題です。日本でも数十万人規模の人材不足が指摘されており、セキュリティスキルを持つエンジニアは極めて高い市場価値を持ちます。",
      },
      {
        title: "すべてのエンジニアに必要な基礎スキル",
        description:
          "セキュリティは専門職だけのものではありません。フロントエンド、バックエンド、インフラ、どの分野のエンジニアもセキュリティの基礎知識は必須です。セキュリティを理解しているエンジニアは、あらゆるチームで重宝されます。",
      },
      {
        title: "高い年収と安定したキャリア",
        description:
          "セキュリティエンジニアは、IT業界の中でも高い年収水準にあります。企業のセキュリティ対策は景気に左右されにくいため、長期的に安定したキャリアを築けるのも魅力です。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでセキュリティを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、上級Phaseのカリキュラムとしてセキュリティを学びます。Web開発の基礎を身につけた上で、実際の攻撃手法を理解し、それに対する防御策を実装する実践的な学習を行います。",
      "座学だけでなく、意図的に脆弱性を持たせた練習環境（DVWA等）を使った実践演習も含まれているため、攻撃者の視点と防御者の視点の両方からセキュリティを理解できます。",
    ],
    topics: [
      "OWASP Top 10の理解と対策",
      "SQLインジェクション・XSS・CSRFの攻撃と防御",
      "認証・認可（OAuth 2.0 / JWT / セッション管理）",
      "暗号化の基礎（ハッシュ・対称鍵・公開鍵暗号）",
      "HTTPSとTLS/SSLの仕組み",
      "セキュアコーディングの原則と実践",
      "実践プロジェクト：脆弱性診断とセキュリティレポート作成",
    ],
  },
  careerPaths: {
    heading: "セキュリティを活かせるキャリア",
    items: [
      {
        title: "セキュリティエンジニア",
        description:
          "Webアプリケーションやインフラのセキュリティ設計・脆弱性診断・対策実装を担当します。ペネトレーションテストやセキュリティ監査を通じて、組織のセキュリティレベルを向上させます。",
        salary: "550〜1,000万円",
      },
      {
        title: "SOCアナリスト",
        description:
          "Security Operations Center（SOC）でセキュリティインシデントの検知・分析・対応を担当します。ログ分析やSIEMツールを活用し、24時間体制でシステムの安全を守ります。",
        salary: "500〜800万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "8〜14ヶ月（範囲が広く体系的に学びにくい）",
        luagate: "4〜5ヶ月（Web開発と連動して効率的に学習）",
      },
      {
        label: "挫折率",
        selfStudy: "攻撃手法の理解が浅いまま防御策を暗記しがち",
        luagate: "実際の攻撃を体験し、原理から防御策を理解",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "練習環境の構築自体が難しい",
        luagate: "用意された脆弱性演習環境で安全に実践",
      },
      {
        label: "質問対応",
        selfStudy: "セキュリティの正解・不正解の判断が難しい",
        luagate: "セキュリティ経験豊富なメンターが具体的に指導",
      },
      {
        label: "就職サポート",
        selfStudy: "セキュリティ分野の求人の探し方がわからない",
        luagate: "セキュリティ企業とのネットワークを活かした就職支援",
      },
    ],
  },
  faq: [
    {
      question: "セキュリティを学ぶのにプログラミング経験は必要ですか？",
      answer:
        "Web開発の基礎知識があると学習がスムーズです。攻撃手法を理解するにはHTML、JavaScript、SQL、HTTPの基本的な知識が必要です。ゆめさくエンジニアではWeb開発を学んだ上でセキュリティに進むため、前提知識は自然と身につきます。",
    },
    {
      question: "セキュリティの学習で違法行為にならないか心配です",
      answer:
        "ゆめさくエンジニアでは意図的に脆弱性を持たせた練習環境（ローカル環境）で学習するため、法的リスクはありません。実際のWebサイトに対する無許可の攻撃は違法ですが、教育目的の閉じた環境での学習は問題ありません。",
    },
    {
      question: "セキュリティ資格（情報処理安全確保支援士等）の取得に役立ちますか？",
      answer:
        "はい、ゆめさくエンジニアで学ぶセキュリティの基礎知識は、情報処理安全確保支援士やCompTIA Security+などの資格取得にも大いに役立ちます。実践的な知識があることで、資格の学習もより効率的に進められます。",
    },
    {
      question: "セキュリティエンジニアは女性にも向いていますか？",
      answer:
        "もちろんです。セキュリティ分野は論理的思考力と細部への注意力が求められる職種で、性別を問わず活躍できます。リモートワークとの親和性も高く、ワークライフバランスを大切にしながらキャリアを築ける分野です。",
    },
    {
      question: "セキュリティだけで就職できますか？",
      answer:
        "セキュリティ専門の職種もありますが、Web開発やインフラのスキルと組み合わせることで、より幅広い選択肢が広がります。ゆめさくエンジニアでは開発スキルとセキュリティ知識を両方身につけるため、市場価値の高いエンジニアを目指せます。",
    },
  ],
  relatedLanguages: [
    { slug: "linux", name: "Linux" },
    { slug: "aws", name: "AWS" },
    { slug: "system-design", name: "システム設計" },
    { slug: "docker", name: "Docker" },
  ],
};
